import axiosInstance from "@/axios";

export const state = {
    usersData: [],
    usersLoading: false,
    usersMsg: null,
    errorMsg: null,
    successMsg: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 15,
        hasNext: false,
        hasPrev: false
    }
}

export const mutations = {
    SET_USERS_DATA(state, usersData) {
        state.usersData = usersData
    },
    SET_LOADING(state, usersLoading) {
        state.usersLoading = usersLoading
    },
    SET_USERS_MSG(state, usersMsg) {
        state.usersMsg = usersMsg
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = { ...pagination }
    },
    ADD_USER(state, user) {
        if (Array.isArray(state.usersData)) {
            state.usersData = [user, ...state.usersData];
        }
    },
    UPDATE_USER(state, updatedUser) {
        if (Array.isArray(state.usersData)) {
            const index = state.usersData.findIndex(u => u.id === updatedUser.id);
            if (index !== -1) {
                state.usersData[index] = { ...state.usersData[index], ...updatedUser };
            }
        }
    },
    DELETE_USER(state, userId) {
        if (Array.isArray(state.usersData)) {
            state.usersData = state.usersData.filter(u => u.id !== userId);
        }
    },
    SET_ERROR_MSG(state, errorMsg) {
        state.errorMsg = errorMsg
    },
    SET_SUCCESS_MSG(state, successMsg) {
        state.successMsg = successMsg
    },
}

export const getters = {
    usersData: state => state.usersData,
    usersLoading: state => state.usersLoading,
    usersMsg: state => state.usersMsg,
    usersPagination: state => state.pagination,
    errorMsg: state => state.errorMsg,
    successMsg: state => state.successMsg,
}

export const actions = {
    async getUsersData({ commit }, params = {}) {
        const { page = 1, perPage = 15, ...filterParams } = params;

        commit('SET_LOADING', true);

        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/users`, {
                params: { page, perPage, ...filterParams }
            });

            // axios interceptor returns response.data directly
            let responseData = response;

            // Normalise top-level paginated response from Laravel
            if (response && response.data && response.links && response.meta) {
                responseData = {
                    data: response.data,
                    links: response.links,
                    meta: response.meta
                };
            }

            let usersData = [];
            let paginationData = {
                currentPage: parseInt(page) || 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: parseInt(perPage) || 15,
                hasNext: false,
                hasPrev: false
            };

            if (responseData && responseData.data && Array.isArray(responseData.data)) {
                usersData = responseData.data;
                const meta = responseData.meta;
                const links = responseData.links;

                if (meta) {
                    paginationData = {
                        currentPage: parseInt(meta.current_page) || parseInt(page) || 1,
                        totalPages: parseInt(meta.last_page) || 1,
                        totalItems: parseInt(meta.total) || usersData.length,
                        itemsPerPage: parseInt(meta.per_page) || parseInt(perPage) || 15,
                        hasNext: !!(links?.next || (meta.current_page && meta.last_page && meta.current_page < meta.last_page)),
                        hasPrev: !!(links?.prev || (meta.current_page && meta.current_page > 1))
                    };
                }
            } else if (Array.isArray(responseData)) {
                usersData = responseData;
                paginationData = {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: usersData.length,
                    itemsPerPage: usersData.length || parseInt(perPage) || 15,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                usersData = responseData?.data || responseData || [];
            }

            commit('SET_USERS_DATA', usersData);
            commit('SET_PAGINATION', paginationData);
            commit('SET_USERS_MSG', null);
        } catch (error) {
            commit('SET_USERS_MSG', error.response?.data?.message || 'Failed to fetch users');
            commit('SET_USERS_DATA', []);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createUser({ commit }, userData) {
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);

        try {
            const response = await axiosInstance({ requiresAuth: true }).post(`/agent/users`, userData);
            const newUser = response.data || response;
            commit('ADD_USER', newUser);
            commit('SET_SUCCESS_MSG', 'User created successfully');
            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while creating the user';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        }
    },

    async updateUser({ commit }, userData) {
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);

        try {
            const { id, ...payload } = userData;
            const response = await axiosInstance({ requiresAuth: true }).patch(`/agent/users/${id}`, payload);
            const updated = (response.data || response) || userData;
            commit('UPDATE_USER', { ...userData, ...updated });
            commit('SET_SUCCESS_MSG', 'User updated successfully');
            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while updating the user';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        }
    },

    async deleteUser({ commit }, userId) {
        commit('SET_LOADING', true);
        try {
            const response = await axiosInstance({ requiresAuth: true }).delete(`/agent/users/${userId}`);
            commit('DELETE_USER', userId);
            commit('SET_USERS_MSG', 'User deleted successfully');
            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while deleting the user';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },
}
